import {
  APP_INITIALIZER,
  enableProdMode,
  ErrorHandler,
  importProvidersFrom,
  inject,
  isDevMode,
  provideZoneChangeDetection,
} from '@angular/core';
import { AppComponent } from './app/app.component';
import { environment } from './environments/environment';
import { bootstrapApplication } from '@angular/platform-browser';
import {
  provideRouter,
  withEnabledBlockingInitialNavigation,
  withInMemoryScrolling,
  withRouterConfig,
} from '@angular/router';
import {
  AppConfigService,
  AuthService,
  ThemeService,
  UrlRewriteGuard,
} from '@intellio/shared/services';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { InMemoryCache } from '@apollo/client/cache';
import {
  AppConfig,
  ENVIRONMENT,
  FORM_PARTIAL,
  MOCKED_SUPPLEMENTAL_FORMS,
  TENANT_FORM_PARTIAL,
} from '@intellio/shared/models';
import { ApplicationinsightsAngularpluginErrorService } from '@microsoft/applicationinsights-angularplugin-js';
import { provideApollo } from 'apollo-angular';
import formPartials from './assets/configs/form-partials.json';
import mockedSpplementalForms from './assets/configs/mocked-supplemental-forms.json';
import tenantFormPartials from './assets/configs/tenant-form-partials.json';
import { initApp } from './app/init-app';
import { SSELink } from './app/sse-link';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { provideState, provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { RootEffects } from './app/+state/root.effects';
import { APIErrorInterceptor } from 'libs/shared/services/src/lib/api-error.interceptor';
import { APIInterceptor } from 'libs/shared/services/src/lib/api.interceptor';
import { LoadingBarInterceptor } from 'libs/shared/services/src/lib/loading-bar.interceptor';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  AppBootstrapResolver,
  SharedResolversModule,
} from '@intellio/shared/resolvers';
import * as fromRoot from './app/+state/root.reducer';
import {
  ApplicationFacade,
  QueueFilterFacade,
  ViewHeaderFacade,
  SupportCenterFacade,
  ApplicationStatusesFacade,
  AccountEffects,
  ApplicationEffects,
  AuthEffects,
  QueueFilterEffects,
  SupportCenterEffects,
  ViewHeaderEffects,
  ApplicationStatusesEffects,
  ModuleConfigsFacade,
  ModuleConfigsEffects,
} from '@intellio/shared/state-mgmt';
import * as fromApplication from '../../../libs/shared/state-mgmt/src/lib/application/application.reducer';
import * as fromAccount from '../../../libs/shared/state-mgmt/src/lib/account/account.reducer';
import * as fromAuth from '../../../libs/shared/state-mgmt/src/lib/auth/auth.reducer';
import * as fromQueueFilter from '../../../libs/shared/state-mgmt/src/lib/queue-filter/queue-filter.reducer';
import * as fromSupportCenter from '../../../libs/shared/state-mgmt/src/lib/support-center/support-center.reducer';
import * as fromViewHeader from '../../../libs/shared/state-mgmt/src/lib/view-header/view-header.reducer';
import * as fromApplicationStatuses from '../../../libs/shared/state-mgmt/src/lib/application-statuses/application-statuses.reducer';
import * as fromModuleConfigs from '../../../libs/shared/state-mgmt/src/lib/module-configs/module-configs.reducer';
import { MarkdownService, provideMarkdown } from 'ngx-markdown';
import { FormlyModule } from '@ngx-formly/core';

declare const config: AppConfig;

if (environment.production) {
  enableProdMode();
}

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(
    http,
    '/assets/use-case-translations/',
    '.json'
  );
}

bootstrapApplication(AppComponent, {
  providers: !environment.production
    ? [
        provideRouter(
          [
            {
              path: '',
              canActivate: [UrlRewriteGuard],
              loadChildren: () =>
                import('@intellio/connect/root').then(
                  (m) => m.ConnectRootModule
                ),
              resolve: {
                data: AppBootstrapResolver,
              },
            },
          ],
          withEnabledBlockingInitialNavigation(),
          withInMemoryScrolling({
            anchorScrolling: 'enabled',
            scrollPositionRestoration: 'enabled',
          }),
          withRouterConfig({ paramsInheritanceStrategy: 'always' })
          // scrollOffset: [0, 64], // [x, y] - adjust scroll offset
        ),
        provideStore(),
        provideState(fromRoot.ROOT_FEATURE_KEY, fromRoot.reducer),
        provideState(fromAccount.ACCOUNT_FEATURE_KEY, fromAccount.reducer),
        provideState(
          fromApplication.APPLICATION_FEATURE_KEY,
          fromApplication.reducer
        ),
        provideState(
          fromApplicationStatuses.APPLICATION_STATUSES_FEATURE_KEY,
          fromApplicationStatuses.reducer
        ),
        provideState(fromAuth.AUTH_FEATURE_KEY, fromAuth.reducer),
        provideState(
          fromQueueFilter.QUEUE_FILTER_FEATURE_KEY,
          fromQueueFilter.reducer
        ),
        provideState(
          fromSupportCenter.SUPPORT_CENTER_FEATURE_KEY,
          fromSupportCenter.reducer
        ),
        provideState(
          fromViewHeader.VIEW_HEADER_FEATURE_KEY,
          fromViewHeader.reducer
        ),
        provideState(
          fromModuleConfigs.MODULE_CONFIGS_FEATURE_KEY,
          fromModuleConfigs.reducer
        ),
        provideEffects(AccountEffects),
        provideEffects(ApplicationEffects),
        provideEffects(ApplicationStatusesEffects),
        provideEffects(AuthEffects),
        provideEffects(QueueFilterEffects),
        provideEffects(SupportCenterEffects),
        provideEffects(ViewHeaderEffects),
        provideEffects(ModuleConfigsEffects),
        provideEffects(RootEffects),
        provideAnimations(),
        QueueFilterFacade,
        ViewHeaderFacade,
        ApplicationFacade,
        SupportCenterFacade,
        ApplicationStatusesFacade,
        ModuleConfigsFacade,
        CurrencyPipe,
        DatePipe,
        SharedResolversModule,
        MarkdownService,
        provideMarkdown(),
        FormlyModule.forRoot({
          extras: { lazyRender: true },
          validationMessages: [
            { name: 'phone', message: 'Phone number is invalid' },
          ],
        }).providers,
        provideHttpClient(withInterceptorsFromDi()),
        {
          provide: HTTP_INTERCEPTORS,
          useClass: LoadingBarInterceptor,
          multi: true,
        },
        {
          provide: HTTP_INTERCEPTORS,
          useClass: APIInterceptor,
          multi: true,
        },
        {
          provide: HTTP_INTERCEPTORS,
          useClass: APIErrorInterceptor,
          multi: true,
        },
        TranslateModule.forRoot({
          loader: {
            provide: TranslateLoader,
            useFactory: HttpLoaderFactory,
            deps: [HttpClient],
          },
        }).providers,
        provideStoreDevtools({
          maxAge: 25,
          logOnly: !isDevMode(),
          autoPause: true,
          trace: false,
          traceLimit: 75,
          connectInZone: true,
        }),
        { provide: ENVIRONMENT, useValue: environment },
        { provide: FORM_PARTIAL, useValue: formPartials },
        {
          provide: MOCKED_SUPPLEMENTAL_FORMS,
          useValue: mockedSpplementalForms,
        },
        { provide: TENANT_FORM_PARTIAL, useValue: tenantFormPartials },
        {
          provide: APP_INITIALIZER,
          useFactory: initApp,
          multi: true,
          deps: [ThemeService, AppConfigService],
        },
        provideApollo(() => {
          const authService = inject(AuthService);
          const sse = new SSELink({
            url: `${
              environment.production ? config?.url : environment.config.url
            }/api/events/graphql`,
            headers: () => {
              if (authService) {
                return {
                  Authorization: `bearer ${authService.accessToken}`,
                  'X-Tenant': `${
                    environment.production
                      ? config?.tenant
                      : environment.config.tenant
                  }`,
                };
              } else {
                return {
                  'X-Tenant': `${
                    environment.production
                      ? config?.tenant
                      : environment.config.tenant
                  }`,
                };
              }
            },
          });
          return {
            cache: new InMemoryCache(),
            link: sse,
          };
        }),
        provideZoneChangeDetection()
      ]
    : [
        provideRouter(
          [
            {
              path: '',
              canActivate: [UrlRewriteGuard],
              loadChildren: () =>
                import('@intellio/connect/root').then(
                  (m) => m.ConnectRootModule
                ),
              resolve: {
                data: AppBootstrapResolver,
              },
            },
          ],
          withEnabledBlockingInitialNavigation(),
          withInMemoryScrolling({
            anchorScrolling: 'enabled',
            scrollPositionRestoration: 'enabled',
          }),
          withRouterConfig({ paramsInheritanceStrategy: 'always' })
          // scrollOffset: [0, 64], // [x, y] - adjust scroll offset
        ),
        provideStore(),
        provideState(
          fromApplication.APPLICATION_FEATURE_KEY,
          fromApplication.reducer
        ),
        provideState(
          fromApplicationStatuses.APPLICATION_STATUSES_FEATURE_KEY,
          fromApplicationStatuses.reducer
        ),
        provideEffects(ApplicationEffects),
        provideEffects(ApplicationStatusesEffects),
        provideState(fromAccount.ACCOUNT_FEATURE_KEY, fromAccount.reducer),
        provideEffects(AccountEffects),
        provideState(fromAuth.AUTH_FEATURE_KEY, fromAuth.reducer),
        provideEffects(AuthEffects),
        provideState(
          fromQueueFilter.QUEUE_FILTER_FEATURE_KEY,
          fromQueueFilter.reducer
        ),
        provideEffects(QueueFilterEffects),
        provideState(
          fromViewHeader.VIEW_HEADER_FEATURE_KEY,
          fromViewHeader.reducer
        ),
        provideEffects(ViewHeaderEffects),
        provideState(fromRoot.ROOT_FEATURE_KEY, fromRoot.reducer),
        provideEffects(RootEffects),
        provideState(
          fromSupportCenter.SUPPORT_CENTER_FEATURE_KEY,
          fromSupportCenter.reducer
        ),
        provideEffects(SupportCenterEffects),
        provideState(
          fromModuleConfigs.MODULE_CONFIGS_FEATURE_KEY,
          fromModuleConfigs.reducer
        ),
        provideEffects(ModuleConfigsEffects),
        provideAnimations(),
        importProvidersFrom([
          FormlyModule.forRoot({
            extras: { lazyRender: true },
            validationMessages: [
              { name: 'phone', message: 'Phone number is invalid' },
            ],
          }),
        ]),
        QueueFilterFacade,
        ViewHeaderFacade,
        ApplicationFacade,
        ApplicationStatusesFacade,
        ModuleConfigsFacade,
        SupportCenterFacade,
        CurrencyPipe,
        SharedResolversModule,
        MarkdownService,
        provideMarkdown(),
        provideHttpClient(withInterceptorsFromDi()),
        {
          provide: HTTP_INTERCEPTORS,
          useClass: LoadingBarInterceptor,
          multi: true,
        },
        {
          provide: HTTP_INTERCEPTORS,
          useClass: APIInterceptor,
          multi: true,
        },
        {
          provide: HTTP_INTERCEPTORS,
          useClass: APIErrorInterceptor,
          multi: true,
        },
        TranslateModule.forRoot({
          loader: {
            provide: TranslateLoader,
            useFactory: HttpLoaderFactory,
            deps: [HttpClient],
          },
        }).providers,
        provideStoreDevtools({
          maxAge: 25,
          logOnly: !isDevMode(),
          autoPause: true,
          trace: false,
          traceLimit: 75,
          connectInZone: true,
        }),
        { provide: ENVIRONMENT, useValue: environment },
        { provide: FORM_PARTIAL, useValue: formPartials },
        {
          provide: MOCKED_SUPPLEMENTAL_FORMS,
          useValue: mockedSpplementalForms,
        },
        { provide: TENANT_FORM_PARTIAL, useValue: tenantFormPartials },
        {
          provide: APP_INITIALIZER,
          useFactory: initApp,
          multi: true,
          deps: [ThemeService, AppConfigService],
        },
        CurrencyPipe,
        DatePipe,
        provideApollo(() => {
          const authService = inject(AuthService);
          const sse = new SSELink({
            url: `${
              environment.production ? config?.url : environment.config.url
            }/api/events/graphql`,
            headers: () => {
              if (authService) {
                return {
                  Authorization: `bearer ${authService.accessToken}`,
                  'X-Tenant': `${
                    environment.production
                      ? config?.tenant
                      : environment.config.tenant
                  }`,
                };
              } else {
                return {
                  'X-Tenant': `${
                    environment.production
                      ? config?.tenant
                      : environment.config.tenant
                  }`,
                };
              }
            },
          });
          return {
            cache: new InMemoryCache(),
            link: sse,
          };
        }),
        {
          provide: ErrorHandler,
          useClass: ApplicationinsightsAngularpluginErrorService,
        },
        provideZoneChangeDetection()
      ],
});
